import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { zip, firstValueFrom } from 'rxjs'
import { getTikTokProfileSettings } from 'services/compose/tiktok/actions'
import { getTikTokProfilePrivacyOptions } from 'services/compose/tiktok/net'
import { hasSavedTikTokSettings, normalizeTikTokPrivacyOptions, reconcileTikTokSettings } from 'services/compose/tiktok/utils'
import { TikTokPostProfileSettings, TikTokPrivacyOptions } from 'shared/types'
import { StoreThunkDispatch } from 'store/state'

export type TikTokData = {
  settings: TikTokPostProfileSettings,
  options: TikTokPrivacyOptions,
  /** Whether "Remember this choice" already stored a record for this profile. */
  hasSavedSettings: boolean
}

export function useTikTokDataFetch(ppids: string): [Record<string, TikTokData>, boolean] {
  const [tiktokData, setTiktokData] = useState<Record<string, TikTokData>>({})
  const [loading, setLoading] = useState(true)

  const dispatch = useDispatch<StoreThunkDispatch>()

  useEffect(() => {
    let cancelled = false
    // An empty ppids string would otherwise split to [''] and fire requests for an empty profile id.
    const ids = ppids.split(',').filter(Boolean)
    // Entries of deselected profiles would otherwise survive - the fetch below only merges into the map.
    setTiktokData(current => {
      const stale = Object.keys(current).filter(ppid => !ids.includes(ppid))
      if (stale.length === 0) {
        return current
      }
      const next = { ...current }
      stale.forEach(ppid => delete next[ppid])
      return next
    })
    if (ids.length === 0) {
      setLoading(false)
      return undefined
    }
    const fetchTikTokData = async () => {
      setLoading(true)
      await Promise.all(ids.map(ppid => {
        return firstValueFrom(zip(dispatch(getTikTokProfilePrivacyOptions(ppid)), dispatch(getTikTokProfileSettings(ppid))))
          .then(([options, saved]) => {
            if (cancelled) {
              return
            }
            const nextOptions = normalizeTikTokPrivacyOptions(options.settings)
            setTiktokData(current => ({
              ...current,
              [ppid]: {
                options: nextOptions,
                settings: reconcileTikTokSettings({ ppid, options: nextOptions, saved }),
                hasSavedSettings: hasSavedTikTokSettings(saved)
              }
            }))
          })
      }))
        .finally(() => {
          if (!cancelled) {
            setLoading(false)
          }
        })
    }
    fetchTikTokData()

    return () => {
      cancelled = true
    }
  }, [dispatch, ppids])

  return [tiktokData, loading]
}
